/**
 * Edit dialog for one essential. Lets the user rename it, change its
 * category, edit notes, and manage default purchase links. Renaming an
 * essential cascades the new name across every furniture-project room
 * item that referenced it (handled server-side).
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import {
  upsertFurnitureEssentialLinks,
  deleteFurnitureEssentialLinks,
  renameFurnitureEssential,
} from '@/lib/api';

const blankLink = () => ({ id: undefined, label: '', url: '', price: '' });
const cloneLinks = (ls) => (ls || []).map((l) => ({
  id: l.id,
  label: l.label || '',
  url: l.url || '',
  price: l.price === null || l.price === undefined ? '' : String(l.price),
}));

export function EssentialEditDialog({
  open, onOpenChange, entry, categories = [], onChanged,
}) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [notes, setNotes] = useState('');
  const [links, setLinks] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!entry) return;
    setName(entry.name || '');
    setCategory(entry.category || '');
    setNotes(entry.notes || '');
    setLinks(cloneLinks(entry.links));
  }, [entry, open]);

  if (!entry) return null;

  const setLink = (i, patch) => setLinks(ls => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLink = () => setLinks(ls => [...ls, blankLink()]);
  const removeLink = (i) => setLinks(ls => ls.filter((_, idx) => idx !== i));

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Name is required');
      return;
    }
    for (const [i, l] of links.entries()) {
      if (l.url.trim()) continue;
      toast.error(`Link #${i + 1} needs a URL`);
      return;
    }
    setSaving(true);
    try {
      // 1. Rename first if changed (server cascades to project items).
      if (trimmed !== entry.name) {
        await renameFurnitureEssential(entry.name, trimmed);
      }
      // 2. Save category / notes / links under the (possibly new) name.
      await upsertFurnitureEssentialLinks(trimmed, {
        category: category.trim() || null,
        notes,
        links: links.map(l => ({
          id: l.id,
          label: l.label.trim(),
          url: l.url.trim(),
          price: l.price === '' ? null : Number(l.price),
        })),
      });
      toast.success('Saved');
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${entry.name}"? This removes the entry from the library only — it does NOT remove items already in projects.`)) return;
    setSaving(true);
    try {
      await deleteFurnitureEssentialLinks(entry.name);
      toast.success('Deleted');
      onChanged?.();
      onOpenChange(false);
    } catch {
      toast.error('Delete failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="essential-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit essential</DialogTitle>
          <DialogDescription>
            Renaming cascades to every project room item that uses this name.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Pillows"
                data-testid="essential-edit-name"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Category</Label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="essential-edit-category"
              >
                <option value="">— Uncategorized —</option>
                {categories.map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Short description, sizing tips, anything useful…"
              rows={2}
              data-testid="essential-edit-notes"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-[#5b6773] font-semibold">Default purchase links</Label>
              <span className="text-[11px] text-[#9ca3af]">First link's price auto-fills new room items</span>
            </div>
            {links.length === 0 && (
              <p className="text-xs text-[#9ca3af] italic px-1">No links yet — click "Add link" to add one.</p>
            )}
            {links.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <Input
                  value={l.label}
                  onChange={(e) => setLink(i, { label: e.target.value })}
                  placeholder="Label (e.g. Amazon)"
                  className="col-span-3 h-8 text-xs"
                  data-testid={`essential-edit-link-label-${i}`}
                />
                <div className="col-span-6 flex items-center gap-1">
                  <Input
                    value={l.url}
                    onChange={(e) => setLink(i, { url: e.target.value })}
                    placeholder="https://…"
                    className="h-8 text-xs flex-1"
                    data-testid={`essential-edit-link-url-${i}`}
                  />
                  {l.url.trim() && (
                    <a href={l.url.startsWith('http') ? l.url : `https://${l.url}`} target="_blank" rel="noopener noreferrer" className="text-[#2f6f8f] hover:opacity-70" aria-label="Open link">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
                <Input
                  type="number" step="0.01" min="0"
                  value={l.price}
                  onChange={(e) => setLink(i, { price: e.target.value })}
                  placeholder="Price"
                  className="col-span-2 h-8 text-xs"
                  data-testid={`essential-edit-link-price-${i}`}
                />
                <button
                  type="button"
                  onClick={() => removeLink(i)}
                  className="col-span-1 text-[#a8324a] opacity-60 hover:opacity-100 flex justify-center"
                  aria-label="Remove link"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={addLink} data-testid="essential-edit-add-link">
              <Plus className="h-3 w-3 mr-1" /> Add link
            </Button>
          </div>
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={remove} disabled={saving} data-testid="essential-edit-delete">
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="essential-edit-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
